import { evaluateClientQuota } from '@frontend/api-client';
import type {
  ImageKitAuthentication,
  LocalFile,
  RetrieveThreadResponse,
  ThreadMessage,
  ThreadReferenceForTask,
  ThreadRequestType,
  ThreadTaskSuggestionMessage,
} from '@frontend/api-types';
import { last, match, objectToStrJson, P } from '@frontend/duck-tape';
import {
  convertImageKitFilesToThreadAttachments,
  Fragment,
  getImageKitImageUrl,
  uploadFilesToImageKit,
  useEffect,
  useRef,
  useState,
  useVisibilityController,
} from '@frontend/react';
import type { ThreadInputMessageState } from '@frontend/react-domain';
import { useThreadChat, useThreadLinks } from '@frontend/react-domain';
import { useAuthSlice } from '@frontend/redux';
import type { FlatListProps, ModalProps } from '@frontend/web-react';
import { FlatList, Modal, useScrollView, useShortcut, YStack } from '@frontend/web-react';
import { ChatFooter } from '@frontend/web/components';
import {
  useAnalytics,
  useCreateTaskContext,
  useCreateThreadMutation,
  useLazyAuthenticateImageKitQuery,
  useLazyRetrieveThreadEntityLinksQuery,
  useLazyRetrieveThreadQuery,
  useRetrieveClientStatusQuery,
} from '@frontend/web/hooks';
import { disconnectSocket, getSocket, handleMutation, initializeSocket } from '@frontend/web/utils';
import { useNavigate } from '@tanstack/react-router';
import { ThreadChatWhisper } from './ThreadChatWhisper';
import { ThreadLander } from './ThreadLander';
import { ThreadLinksModal } from './ThreadLinksModal';
import type { ThreadMarkdownRendererProps } from './ThreadMarkdownRenderer';
import { ThreadSuggestionMessage } from './ThreadSuggestionMessage';
import { ThreadTextMessage } from './ThreadTextMessage';

type CaptiveThreadsModalProps = Pick<ModalProps, 'isOpen' | 'onClose'> & {
  onThreadRequestTypeChange: (type: ThreadRequestType) => void;
  // Hack to update parent state
  refetchFlag: boolean;
  setRefetchFlag: (flag: boolean) => void;
};

export const CaptiveThreadsModal = ({
  isOpen,
  onClose,
  onThreadRequestTypeChange,
  refetchFlag,
  setRefetchFlag,
}: CaptiveThreadsModalProps) => {
  const navigate = useNavigate();
  const [isThreadLinksModalOpen, { close: closeThreadLinksModal, open: openThreadLinksModal }] =
    useVisibilityController();
  const { openCreateTaskModal } = useCreateTaskContext();
  const { token } = useAuthSlice();
  const [createThread, { isLoading: isCreateThreadLoading }] = useCreateThreadMutation();
  const [retrieveThreadData] = useLazyRetrieveThreadQuery();
  const [retrieveThreadEntityLinks] = useLazyRetrieveThreadEntityLinksQuery();
  const { track } = useAnalytics();
  const { scrollRef, scrollToBottom } = useScrollView({ onRenderScroll: 'bottom' });
  const { data: clientStatus } = useRetrieveClientStatusQuery();
  const [authenticateImageKit] = useLazyAuthenticateImageKitQuery();
  const isThrottled = Boolean(clientStatus && evaluateClientQuota(clientStatus).isThrottled);

  // Refs
  const textAreaInputRef = useRef<HTMLTextAreaElement>(null);

  // State
  const [threadId, setThreadId] = useState<string>();
  const [inputMessage, setInputMessage] = useState<ThreadInputMessageState>({
    current: '',
    // Refers to message that was used to generate the current AI response, needed for "Retry" logic on error
    inFlight: '',
  });
  const [threadFiles, setThreadFiles] = useState<LocalFile[]>([]);
  const [threadData, setThreadData] = useState<RetrieveThreadResponse>();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  // Helper functions
  const getAndSetThreadData = async () => {
    if (threadId) {
      const { data } = await retrieveThreadData(threadId);
      setThreadData(data);
    }
  };

  const refetchThreadData = async () => {
    await getAndSetThreadData();
  };

  const resetState = () => {
    resetThreadChatState();
    setThreadData(undefined);
    setInputMessage({ current: '', inFlight: '' });
    setThreadId(undefined);
  };

  const closeAndClearModal = () => {
    resetState();
    onClose();
  };

  const onBlurTextInput = () => {
    textAreaInputRef.current?.blur();
  };

  const handleImageKit = async (files: LocalFile[]) => {
    setIsSubmitLoading(true);
    const handleAuthenticateImageKit = async (): Promise<ImageKitAuthentication | undefined> => {
      const { data } = await authenticateImageKit();
      return data;
    };
    const imageKitFiles = await uploadFilesToImageKit(files, handleAuthenticateImageKit);
    setIsSubmitLoading(false);
    return convertImageKitFilesToThreadAttachments(imageKitFiles);
  };

  // Custom hooks
  const {
    interruptStream,
    isAIStreaming,
    messages,
    resetState: resetThreadChatState,
    sendThreadMessage,
    threadRequestType,
  } = useThreadChat({
    convertFilesToThreadAttachments: handleImageKit,
    getImageKitImageUrl: getImageKitImageUrl,
    onBlurTextInput,
    onReceiveMessageSocketUpdate: scrollToBottom,
    onSendFollowUpMessage: ({ message }) => {
      closeAndClearModal();
      navigate({
        params: {
          threadId: threadId!,
        },
        search: {
          autoMessageSendContent: message,
        },
        to: '/app/thread/$threadId',
      });
    },
    refetchThread: refetchThreadData,
    scrollToBottom,
    setInputMessage,
    setThreadFiles,
    socket: {
      disconnectSocket,
      getSocket,
      initializeSocket,
    },
    threadData,
    threadId,
    token,
  });

  const { isThreadEntityLinksPollingLoading, linksData, queryThreadEntityLinks, setLinksData } = useThreadLinks({
    retrieveThreadEntityLinks: async (uuid, useCache) => (await retrieveThreadEntityLinks(uuid, useCache)).data,
  });

  const onClickEntity: ThreadMarkdownRendererProps['onClickLink'] = async (linkData) => {
    openThreadLinksModal();
    queryThreadEntityLinks(linkData);
  };

  const onClickTaskSuggestion = (researchThreadReferenceData: ThreadReferenceForTask) => {
    match(researchThreadReferenceData.suggestion)
      .with({ recurrenceId: P.string }, ({ recurrenceId }) => {
        closeAndClearModal();
        navigate({
          params: { recurrenceId },
          to: '/app/recurrence/$recurrenceId',
        });
      })
      .with({ taskId: P.string }, ({ taskId }) => {
        closeAndClearModal();
        navigate({
          params: researchThreadReferenceData.suggestion.isDraftTask ? { draftTaskId: taskId } : { taskId },
          to: researchThreadReferenceData.suggestion.isDraftTask ? '/app/draft-task/$draftTaskId' : '/app/task/$taskId',
        });
      })
      .otherwise(() => {
        const initialMessage = last(messages) as ThreadMessage;
        const useInitialMessageAsDescription = messages.length === 3 && threadRequestType === 'create';

        openCreateTaskModal({
          initialData: {
            description: useInitialMessageAsDescription
              ? (initialMessage.content as string)
              : researchThreadReferenceData.suggestion.title,
            name: researchThreadReferenceData.suggestion.title,
            researchThreadId: threadId,
            researchThreadReferenceData: researchThreadReferenceData,
          },
          isRecurrence: researchThreadReferenceData.suggestion.isRecurrence,
          onCreateCallback: () => {
            track({
              data: {
                requestType: threadRequestType!,
                threadId: threadId!,
              },
              event: 'captiveThreadTaskCreatedFromSuggestion',
              topic: 'research',
            });
          },
          stepsToSkip: ['description'],
        });
      });
  };

  const onSubmitSecondMessage = () => {
    track({
      data: {
        requestType: threadRequestType!,
        threadId: threadId!,
      },
      event: 'captiveThreadFollowUpMessageSubmitted',
      topic: 'research',
    });
    navigate({
      params: { threadId: threadId! },
      search: {
        autoMessageSendContent: inputMessage.current,
        files: objectToStrJson(threadFiles),
      },
      to: '/app/thread/$threadId',
    });
    closeAndClearModal();
  };

  const onSubmitFirstMessage = async () => {
    return handleMutation({
      mutation: async () => {
        return createThread().unwrap();
      },
      onSuccess: ({ id }) => {
        sendThreadMessage(inputMessage.current, threadFiles, {
          threadId: id,
        });
        setThreadId(id);
        setInputMessage({ current: '', inFlight: '' });
        textAreaInputRef.current?.blur();
      },
      showSuccessToast: false,
    });
  };

  const onSubmitMessage = async () => {
    if (!threadId) {
      await onSubmitFirstMessage();
    } else {
      onSubmitSecondMessage();
    }
  };

  const onChangeText = (messageValue: string) => {
    setInputMessage((cur) => ({ ...cur, current: messageValue }));
  };

  const renderItem: FlatListProps<ThreadMessage>['renderItem'] = ({ index, item: message }) => {
    if (!messages.length) return null;
    const messageIsStreaming = index === 0 && isAIStreaming;
    const reversedIndex = messages.length - 1 - index;
    return match(message)
      .with({ role: 'ai_recommender', type: 'task_suggestions' }, (message) => {
        return (
          <ThreadSuggestionMessage
            isStreaming={messageIsStreaming}
            isThrottled={isThrottled}
            message={message}
            messageIndex={reversedIndex}
            onClickSuggestionCard={onClickTaskSuggestion}
            researchThreadId={threadId!}
            threadRequestType={threadRequestType || threadData?.initialRequestType}
          />
        );
      })
      .with({ role: 'ai_bot', type: 'task_created' }, (message) => {
        return (
          <ThreadChatWhisper
            content={{
              onClickBoldText: () => {
                closeAndClearModal();
                navigate({
                  params: { taskId: message.content.id },
                  to: '/tasks/$taskId',
                });
              },
              taskTitle: message.content.name,
            }}
          />
        );
      })
      .with({ role: 'ai_bot', type: 'draft_task_created' }, (message) => {
        return (
          <ThreadChatWhisper
            content={{
              isDraftTask: true,
              onClickBoldText: () => {
                closeAndClearModal();
                navigate({
                  params: { draftTaskId: message.content.id },
                  to: '/app/draft-task/$draftTaskId',
                });
              },
              taskTitle: message.content.name,
            }}
          />
        );
      })
      .with({ role: 'ai_bot', type: 'recurrence_created' }, (message) => {
        return (
          <ThreadChatWhisper
            content={{
              onClickBoldText: () => {
                closeAndClearModal();
                navigate({
                  params: { recurrenceId: message.content.id },
                  to: '/app/recurrence/$recurrenceId',
                });
              },
              taskTitle: message.content.name,
            }}
          />
        );
      })
      .with({ role: P.union('ai_bot', 'client') }, (message) => (
        <ThreadTextMessage
          hideTime
          index={index}
          isStreaming={messageIsStreaming}
          message={message}
          messagesList={messages}
          onClickLink={onClickEntity}
          threadId={threadId!}
        />
      ))
      .exhaustive();
  };

  // Opens the create task slideup when the first suggestion is received in the creator flow
  useEffect(() => {
    if (threadRequestType === 'create' && messages.length === 3 && !isAIStreaming) {
      const suggestionMessage = messages[0] as ThreadTaskSuggestionMessage;
      const suggestionContent = suggestionMessage.content;
      const initialMessage = last(messages) as ThreadMessage;

      if (suggestionContent.length) {
        const suggestion = suggestionContent[0]!;
        openCreateTaskModal({
          initialData: {
            description: initialMessage.content as string,
            name: suggestion.title,
            researchThreadId: threadId,
            researchThreadReferenceData: {
              id: threadId!,
              // Length of messages is 3
              messageIdx: 2,
              suggestion,
              // Only ever one suggestion in the creator flow
              taskSuggestionIdx: 0,
            },
          },
          isRecurrence: suggestion.isRecurrence,
          onCreateCallback: () => {
            track({
              data: {
                requestType: threadRequestType!,
                threadId: threadId!,
              },
              event: 'captiveThreadTaskCreatedFromSuggestion',
              topic: 'research',
            });
            closeAndClearModal();
          },
          stepsToSkip: ['description'],
        });
      }
    }
  }, [threadRequestType, messages, isAIStreaming]);

  // Resets state anytime modal is closed
  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  useEffect(() => {
    if (threadRequestType) {
      onThreadRequestTypeChange(threadRequestType);
    }
  }, [threadRequestType]);

  useEffect(() => {
    if (refetchFlag) {
      refetchThreadData();
      setRefetchFlag(false);
    }
  }, [refetchFlag]);

  const isTextInputDisabled = isCreateThreadLoading || isSubmitLoading || isAIStreaming;

  useShortcut({
    Enter: {
      isDisabled: isTextInputDisabled,
      onSubmit: onSubmitMessage,
    },
  });

  return (
    <Fragment>
      <Modal
        Footer={
          <YStack>
            <ChatFooter
              filesValue={threadFiles}
              isStreamResponseLoading={isAIStreaming}
              isSubmitLoading={isSubmitLoading || isCreateThreadLoading}
              onChangeFiles={(files) => setThreadFiles(files)}
              onChangeText={onChangeText}
              onClickInterrupt={interruptStream}
              onClickSubmit={onSubmitMessage}
              placeholder="Describe your task..."
              submitDisabled={!inputMessage.current && !isAIStreaming}
              submitShortcut="Enter"
              textInputDisabled={isTextInputDisabled}
              textInputRef={textAreaInputRef}
              textValue={inputMessage.current}
            />
          </YStack>
        }
        bodyClassName="bg-surfaceSecondary"
        confirmOnClose={Boolean(inputMessage.current || messages.length || threadFiles.length)}
        footerClassName="px-md py-md bg-surfaceSecondary"
        isOpen={isOpen}
        onClose={onClose}
        padding={messages.length ? 'md' : 'none'}
        ref={scrollRef}
        scrollViewContainerClassName="max-h-[60vh]"
        size="748"
      >
        <YStack className="gap-y-lg">
          <FlatList
            ListEmptyComponent={
              <ThreadLander setInputMessage={(message) => setInputMessage((prev) => ({ ...prev, current: message }))} />
            }
            contentContainerClassName="gap-y-xl pb-md"
            data={messages}
            inverted
            renderItem={renderItem}
          />
        </YStack>
      </Modal>
      {threadId ? (
        <ThreadLinksModal
          isOpen={isThreadLinksModalOpen}
          isRetrieveLinksLoading={isThreadEntityLinksPollingLoading}
          linkText={linksData?.selectedText}
          onClose={() => {
            closeThreadLinksModal();
            setLinksData({ selectedText: undefined, siteData: [], status: undefined });
          }}
          siteData={linksData.siteData}
          status={linksData?.status}
          threadId={threadId}
        />
      ) : null}
    </Fragment>
  );
};
